// src/pages/RoomQRPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import * as signalR from "@microsoft/signalr";
import GuestLogin from '../components/GuestLogin';

import {
  Row,
  Col,
  Card,
  Typography,
  Space,
  Tag,
  Button,
  Spin,
  Empty,
  Statistic,
  Divider,
  List,
  Drawer,
  Input,
  message as antdMessage,
  notification,
  FloatButton,
  Badge,
  Grid,
  Tabs,
  Modal,
  InputNumber,
  Radio,
  Select,
  Table,
} from "antd";

import {
  BellOutlined,
  CoffeeOutlined,
  SkinOutlined,
  ShoppingOutlined,
  ThunderboltOutlined,
  MessageOutlined,
  SendOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  CustomerServiceOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  FileTextOutlined,
  ToolOutlined,
  RestOutlined,
  LockOutlined,
} from "@ant-design/icons";

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;
const { TextArea } = Input;

/* ---------- helpers ---------- */
const asNum = (v) => Number(v ?? 0);
const money = (n, ccy = "BWP") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(asNum(n));
const fmtDT = (v) => (v ? new Date(v).toLocaleString() : "");

// read a value with multiple possible casings/aliases
const pick = (obj, keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};
// put near your other helpers
const safeParseJSON = (raw) => {
  if (!raw) return null;
  const t = raw.trim();
  if (t === "undefined" || t === "null") return null;       // old/bad saves
  // quick sanity check—valid JSON should start with {, [, or "
  const first = t[0];
  if (!['{','[','"'].includes(first)) return null;
  try { return JSON.parse(t); } catch { return null; }
};

/* ---------- ADDED: normalization helpers ---------- */
const normalizeMenuItem = (x, i, parentServiceId) => {
  const rawId = pick(x, ["menuItemId","MenuItemId","id","Id"]);
  const uid   = mkUid(rawId, i, parentServiceId);
  return {
    uid,                                      // <- use this in UI/state
    menuItemId: Number(rawId) || null,        // <- keep for API
    name: pick(x, ["name","Name"]) || "Item",
    description: pick(x, ["description","Description"]) || "",
    price: asNum(pick(x, ["price","Price"])),
    estimatedReadyTime: pick(x, ["estimatedReadyTime","EstimatedReadyTime","eta"]) ?? null,
  };
};

const mkUid = (rawId, i, parent) => {
  const n = Number(rawId);
  return Number.isFinite(n) && n > 0
    ? `id:${n}`                        // stable when backend id exists
    : `tmp:${parent ?? 'svc'}:${i}`;   // unique fallback for UI
};

const normalizeService = (s) => {
  const serviceId = pick(s, ["serviceId","ServiceId","roomServiceId","RoomServiceId","id","Id"]);
  const rawItems  = pick(s, ["menuItems","MenuItems"]) || [];
  return {
    serviceId,
    name: pick(s, ["name","Name","serviceName","ServiceName"]) || "Service",
    description: pick(s, ["description","Description","serviceDescription","ServiceDescription"]) || "",
    menuItems: Array.isArray(rawItems) ? rawItems.map((it, idx) => normalizeMenuItem(it, idx, serviceId)) : [],
  };
};

const serviceIconMap = {
  "Room Service": <BellOutlined />,
  Minibar: <CoffeeOutlined />,
  "Spa and Wellness": <SkinOutlined />,
  "Laundry Service": <ShoppingOutlined />,
  "Gym Access": <ThunderboltOutlined />,
  Maintenance: <ToolOutlined />,
  Housekeeping: <RestOutlined />,
  default: <BellOutlined />,
};

const orderIconMap = {
  "Room Service": <ShoppingCartOutlined />,
  Minibar: <CoffeeOutlined />,
  default: <ShoppingCartOutlined />,
};
 
 /* ---------- Service Card (responsive) ---------- */
 /* ---------- Service Card (responsive) ---------- */
const ServiceCard = ({ service, onRequest, isMobile, type = "service" }) => {
  const icon =
    type === "order"
      ? orderIconMap[service.name] || orderIconMap.default
      : serviceIconMap[service.name] || serviceIconMap.default;

  // Create a stable click handler that passes the correct service
  const handleClick = React.useCallback(() => {
    //console.log('ServiceCard clicked:', service.name, service.serviceId);
    onRequest(service);
  }, [service, onRequest]);

  return (
    <Card
      hoverable
      style={{
        height: "100%",
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: 16,
        },
      }}
    >
      <div
        style={{
          fontSize: 26,
          width: 48,
          height: 48,
          borderRadius: 10,
          display: "grid",
          placeItems: "center",
          background:
            type === "order"
              ? "linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)"
              : "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
        }}
        aria-hidden
      >
        {icon}
      </div>

      <Space direction="vertical" size={4} style={{ flex: 1, minHeight: 60 }}>
        <Text strong style={{ fontSize: 16 }}>
          {service.name}
        </Text>
        {service.description && (
          <Text
            type="secondary"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              fontSize: 14,
            }}
          >
            {service.description}
          </Text>
        )}
      </Space>

      <Button
        type={type === "order" ? "default" : "primary"}
        block
        onClick={handleClick}
        style={{ borderRadius: 8 }}
      >
        {type === "order" ? "Order Now" : "Request Service"}
      </Button>
    </Card>
  );
};


/** ---------------- Collapsible Real-time mini chart for order activity ---------------- **/
const barColors = {
  pending: "#d9d9d9",
  accepted: "#1677ff",
  completed: "#52c41a",
  rejected: "#ff4d4f",
};
const labelMap = {
  pending: "Pending",
  accepted: "Accepted",
  completed: "Completed",
  rejected: "Rejected",
};

const LiveOrderChart = ({ counts, collapsed, onToggle }) => {
  // Ensure counts always has all required properties, even if zero
  const safeCounts = {
    pending: asNum(counts?.pending) || 0,
    accepted: asNum(counts?.accepted) || 0,
    completed: asNum(counts?.completed) || 0,
    rejected: asNum(counts?.rejected) || 0,
  };

  const total = safeCounts.pending + safeCounts.accepted + safeCounts.completed + safeCounts.rejected;

  // Always show the chart, even with zero data
  const rows = ["pending", "accepted", "completed", "rejected"].map((k) => ({
    key: k,
    label: labelMap[k],
    value: safeCounts[k],
    pct: total ? (safeCounts[k] / total) * 100 : 0,
  }));

  return (
    <Card 
      size="small" 
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>Live Order Activity (this room)</span>
          <Button 
            type="text" 
            size="small" 
            icon={collapsed ? <PlusOutlined /> : <MinusOutlined />}
            onClick={onToggle}
            style={{ padding: 0, width: 24, height: 24 }}
          />
        </Space>
      }
      styles={{ body: { padding: collapsed ? 0 : 12 } }}
    >
      {!collapsed && (
        <>
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            {rows.map((r) => (
              <div key={r.key}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <Text>{r.label}</Text>
                  <Text type="secondary">{r.value}</Text>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 10,
                    borderRadius: 6,
                    background: "#f0f0f0",
                    overflow: "hidden",
                  }}
                  aria-label={`${r.label} ${r.value}`}
                >
                  <div
                    style={{
                      width: `${r.pct}%`,
                      height: "100%",
                      background: barColors[r.key],
                      transition: "width .3s ease",
                    }}
                  />
                </div>
              </div>
            ))}
          </Space>
          
          {/* Show message when no activity yet */}
          {total === 0 && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                No order activity yet
              </Text>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

/** ---------------- SignalR helpers (with auto-fallback paths) ---------------- **/
const HUB_PATH_CANDIDATES = ["/transactionHub", "/hubs/transaction", "/chatHub"];

// Build absolute hub URL(s) from axios base (strips trailing /api)
const computeHubUrlCandidates = () => {
  const apiBase = (axiosInstance.defaults.baseURL || "").replace(/\/+$/, "");
  const origin = apiBase ? apiBase.replace(/\/api($|\/)/, "") : "";
  return HUB_PATH_CANDIDATES.map((p) => (origin ? `${origin}${p}` : p));
};

const startSignalRWithFallback = async () => {
  const urls = computeHubUrlCandidates();
  let lastErr = null;
  for (const url of urls) {
    try {
      const connection = new signalR.HubConnectionBuilder()
        .configureLogging(signalR.LogLevel.Information)
        .withAutomaticReconnect()
        /* CHANGED: include guest token if hub is protected */
        .withUrl(url, {
          withCredentials: true,
          accessTokenFactory: () => localStorage.getItem("guestToken") || undefined,
        })
        .build();

      await connection.start();
      return connection;
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error("Failed to connect to any hub path");
};

const RoomQRPage = () => {
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const queryParams = new URLSearchParams(location.search);

  // Accept both roomId and roomNumber (plus common aliases)
  const roomIdFromQR =
    queryParams.get("roomId") || queryParams.get("rid") || queryParams.get("room_id");

  const roomNumberFromQR =
    queryParams.get("roomNumber") ||
    queryParams.get("room") ||
    queryParams.get("room_no") ||
    queryParams.get("rn");

  const hotelId =
    queryParams.get("hotelId") || queryParams.get("hid") || queryParams.get("hotel_id");

  // Optional: booking filter for order history
  const bookingIdFromQR =
    queryParams.get("bookingId") || queryParams.get("bid") || queryParams.get("booking_id");

  // Guest Login States
  const [isGuestLoggedIn, setIsGuestLoggedIn] = useState(false);
  const [isGuestLoginOpen, setIsGuestLoginOpen] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);

  const [guestDetails, setGuestDetails] = useState(null);
  const [services, setServices] = useState([]);
  const [orderServices, setOrderServices] = useState([]);
  const [guestOrder, setGuestOrder] = useState(null);

  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("error");

  // NEW STATES FOR IMPROVEMENTS
  const [chartCollapsed, setChartCollapsed] = useState(false);
  const [servicesTab, setServicesTab] = useState("services"); // "services" | "orders"

  // Service Request Modal
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceNotes, setServiceNotes] = useState("");
  const [isSubmittingService, setIsSubmittingService] = useState(false);
  // Service Types
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceTypesLoading, setServiceTypesLoading] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [serviceQty, setServiceQty] = useState(null);

  // Order Modal
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrderService, setSelectedOrderService] = useState(null);
  const [orderNotes, setOrderNotes] = useState("");
  const [orderQty, setOrderQty] = useState({});
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);

  // Fulfilment choice
  const [serviceMode, setServiceMode] = useState("RoomService"); // "RoomService" | "Collection"
  const [pickupLocation, setPickupLocation] = useState("Restaurant Counter");

  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");

  // Bottom tabs
  const [bottomTab, setBottomTab] = useState("current");

  // Notifications
  const [api, contextHolder] = notification.useNotification();

  // SignalR connection ref
  const hubRef = useRef(null);

  // Live chart state
  const [orderCounts, setOrderCounts] = useState({
    pending: 0,
    accepted: 0,
    completed: 0,
    rejected: 0,
  });

  // History lists
  const [serviceRequests, setServiceRequests] = useState([]);
  const [serviceRequestsLoading, setServiceRequestsLoading] = useState(false);
  const [ordersList, setOrdersList] = useState([]);
  const [ordersListLoading, setOrdersListLoading] = useState(false);

  const guestName = useMemo(
    () => guestDetails?.fullName || guestDetails?.name || guestInfo?.guestName || "Guest",
    [guestDetails, guestInfo]
  );

  /* ---------- SIMPLIFIED: use normalized menu directly ---------- */
  const orderMenu = useMemo(
    () => (selectedOrderService?.menuItems || []),
    [selectedOrderService]
  );

  const orderItemsCount = useMemo(
  () => orderMenu.reduce((n, m) => n + asNum(orderQty[m.uid] || 0), 0),
  [orderMenu, orderQty]
);


const orderTotal = useMemo(
  () => orderMenu.reduce((sum, m) => sum + asNum(orderQty[m.uid] || 0) * asNum(m.price), 0),
  [orderMenu, orderQty]
);
  const setItemQty = (uid, value) =>
  setOrderQty(prev => ({ ...prev, [uid]: Math.max(0, asNum(value)) }));
const inc = (uid) => setItemQty(uid, asNum(orderQty[uid]) + 1);
const dec = (uid) => setItemQty(uid, Math.max(0, asNum(orderQty[uid]) - 1));


  /* ---------- Guest Login Handler ---------- */
  // In RoomQRPage.jsx - Update the login success handler
 // In RoomQRPage.jsx - Check your onLoginSuccess handler
const handleGuestLoginSuccess = (loginData) => {
  //console.log('📨 handleGuestLoginSuccess received:', loginData);
  
  if (!loginData || !loginData.accessToken) {
    console.error('❌ Invalid login data received:', loginData);
    antdMessage.error('Login failed: Invalid response from server');
    return;
  }

  // Extract data
  const { accessToken, guest } = loginData;
  
  if (!guest) {
    console.warn('⚠️ No guest data in login response, using fallback');
  }

  // Update state IMMEDIATELY
  setIsGuestLoggedIn(true);
  setGuestInfo(guest || {
    guestName: 'Guest',
    roomNumber: 'Unknown',
    hotelId: hotelId
  });
  setIsGuestLoginOpen(false);
  
  // Store tokens
  localStorage.setItem('guestToken', accessToken);
  localStorage.setItem('guestInfo', JSON.stringify(guest));
  axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  
  // Show success message
  antdMessage.success(`Welcome, ${guest?.guestName || guest?.firstName || 'Guest'}!`);
  
  //console.log('✅ Login state updated, fetching room data...');
  
  // Refresh data
  setTimeout(() => {
    fetchRoomData();
  }, 100);
};

  const handleGuestLogout = () => {
    setIsGuestLoggedIn(false);
    setGuestInfo(null);
    setGuestDetails(null);
    setServices([]);
    setOrderServices([]);
    setGuestOrder(null);

    // Stop SignalR and clear ref
    try { hubRef.current?.stop(); } catch {}
    hubRef.current = null;
    
    // Clear guest token
    localStorage.removeItem('guestToken');
    localStorage.removeItem('guestInfo');
    delete axiosInstance.defaults.headers.common['Authorization'];
    
    antdMessage.success('Logged out successfully');
  };

  /* ---------- Chat ---------- */
  const fetchChatMessages = async () => {
    try {
      const res = await axiosInstance.get("/Chat", {
        params: { hotelId: hotelId || guestDetails?.hotelId }
      });
      setChatMessages(Array.isArray(res.data) ? res.data : []);
    } catch {
      // ignore
    }
  };

  const handleSendMessage = async () => {
    const text = chatInput.trim();
    if (!text) return;
    try {
      const roomNumber = guestDetails?.roomNumber || guestInfo?.roomNumber;
      const payload = {
        sender: "guest",
        text,
        room: String(roomNumber),
        hotelId: hotelId || guestDetails?.hotelId || guestInfo?.hotelId,
      };
      const res = await axiosInstance.post("/Chat", payload);
      setChatMessages((prev) => [...prev, res.data ?? payload]);
      setChatInput("");
    } catch {
      antdMessage.error("Failed to send message.");
    }
  };

  useEffect(() => {
    if (!chatOpen) return;
    fetchChatMessages();
    const t = setInterval(fetchChatMessages, 5000);
    return () => clearInterval(t);
  }, [chatOpen]);

  /* ---------- Orders ---------- */
  const fetchGuestOrder = async (rmNo) => {
    try {
      setOrderLoading(true);
      const resolvedHotelId = hotelId || guestDetails?.hotelId || guestInfo?.hotelId;
      const roomNumber = rmNo || guestInfo?.roomNumber;
      
      if (!roomNumber) {
        setGuestOrder(null);
        return;
      }

      const res = await axiosInstance.get(
        `/hotels/${resolvedHotelId}/RestaurantOrders/activeOrder?roomNumber=${encodeURIComponent(roomNumber)}`
      );
      setGuestOrder(res.data || null);
    } catch {
      setGuestOrder(null);
    } finally {
      setOrderLoading(false);
    }
  };

  const fetchOrderById = async (id) => {
    const resolvedHotelId = hotelId || guestDetails?.hotelId || guestInfo?.hotelId;
    const res = await axiosInstance.get(`/hotels/${resolvedHotelId}/RestaurantOrders/${id}`);
    return res.data;
  };

  const setOrderFromId = async (id) => {
    try {
      setOrderLoading(true);
      const data = await fetchOrderById(id);
      setGuestOrder(data);
    } catch {
      setGuestOrder(null);
    } finally {
      setOrderLoading(false);
    }
  };

  // fetch lists (robust with multiple endpoints for RoomServices only)
  const fetchServiceRequests = async (rmNo, rmId) => {
    const roomNumber = rmNo || guestInfo?.roomNumber;
    if (!roomNumber && !rmId) return setServiceRequests([]);
    
    setServiceRequestsLoading(true);
    
    const resolvedHotelId = hotelId || guestDetails?.hotelId || guestInfo?.hotelId;

    const candidates = [
      // Pattern 1: RoomServices with query parameters
      roomNumber && `/RoomServices?roomNumber=${encodeURIComponent(roomNumber)}&hotelId=${encodeURIComponent(resolvedHotelId)}`,
      rmId && `/RoomServices?roomId=${encodeURIComponent(rmId)}&hotelId=${encodeURIComponent(resolvedHotelId)}`,
      
      // Pattern 2: RoomService with query parameters  
      roomNumber && `/RoomService?roomNumber=${encodeURIComponent(roomNumber)}&hotelId=${encodeURIComponent(resolvedHotelId)}`,
      rmId && `/RoomService?roomId=${encodeURIComponent(rmId)}&hotelId=${encodeURIComponent(resolvedHotelId)}`,
    ].filter(Boolean);

    let list = null;

    for (const url of candidates) {
      try {
        const res = await axiosInstance.get(url, {
          validateStatus: (s) => [200, 204, 404].includes(s),
        });
        
        if (res.status === 200 && Array.isArray(res.data)) {
          list = res.data;
          break;
        }
      } catch (error) {
        //console.log(`Error with ${url}:`, error.message);
      }
    }

    const normalized = Array.isArray(list)
      ? list.map((x, idx) => ({
          id:
            pick(x, [
              "roomServiceRequestId",
              "RoomServiceRequestId",
              "requestId",
              "RequestId",
              "id",
              "Id",
            ]) ?? `req-${idx}`,
          serviceType:
            pick(x, [
              "serviceType",
              "ServiceType",
              "type",
              "Type",
              "serviceName",
              "ServiceName",
            ]) || "Service",
          description:
            pick(x, [
              "serviceDescription",
              "ServiceDescription",
              "description",
              "Description",
              "notes",
              "Notes",
            ]) || "",
          status: pick(x, ["status", "Status"]) || "Pending",
          createdDate:
            pick(x, [
              "requestTime",
              "RequestTime",
              "createdDate",
              "CreatedDate",
              "createdAt",
              "CreatedAt",
            ]) || null,
          quantity: asNum(pick(x, ["quantity", "Quantity"])) || null,
        }))
      : [];

    normalized.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
    setServiceRequests(normalized);
    setServiceRequestsLoading(false);
  };
 
  const fetchOrdersList = async (rmNo, bookingId) => {
    setOrdersListLoading(true);

    try {
      let list = null;
      const hotelIdParam = hotelId || guestDetails?.hotelId || guestInfo?.hotelId;
      const roomNumber = rmNo || guestInfo?.roomNumber;

      if (bookingId) {
        // Prefer by booking when provided
        const res = await axiosInstance.get(`/hotels/${hotelIdParam}/RestaurantOrders/by-booking/${encodeURIComponent(bookingId)}`, {
          validateStatus: (s) => [200, 204, 404].includes(s),
        });
        list = res.status === 200 && Array.isArray(res.data) ? res.data : [];
      } else if (roomNumber) {
        // Fallback to by-room
        const res = await axiosInstance.get(
          `/hotels/${hotelIdParam}/RestaurantOrders/by-room?roomNumber=${encodeURIComponent(roomNumber)}`,
          { validateStatus: (s) => [200, 204, 404].includes(s) }
        );
        list = res.status === 200 && Array.isArray(res.data) ? res.data : [];
      } else {
        list = [];
      }

      const normalized = (Array.isArray(list) ? list : []).map((x, idx) => {
        const items =
          pick(x, ["orderItems", "OrderItems", "restaurantOrderItems", "RestaurantOrderItems"]) ||
          [];
        return {
          orderId: pick(x, ["orderId", "OrderId", "id", "Id"]) ?? `order-${idx}`,
          orderType: pick(x, ["orderType", "OrderType", "type", "Type", "name", "Name"]) || "Room Service",
          itemsCount: Array.isArray(items)
            ? items.reduce((n, i) => n + (asNum(pick(i, ["quantity", "Quantity"])) || 0), 0)
            : 0,
          amount:
            asNum(pick(x, ["amount", "Amount", "total", "Total", "grandTotal", "GrandTotal"])) ||
            0,
          status: pick(x, ["status", "Status"]) || "Pending",
          createdDate: pick(x, ["createdDate", "CreatedDate", "createdAt", "CreatedAt"]) || null,
          deliveryMethod: pick(x, ["deliveryMethod", "DeliveryMethod"]) || null,
          pickupLocation: pick(x, ["pickupLocation", "PickupLocation"]) || null,
        };
      });

      normalized.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
      setOrdersList(normalized);

      /* ADDED: seed the live chart from history */
      const nextCounts = { pending: 0, accepted: 0, completed: 0, rejected: 0 };
      for (const o of normalized) {
        const s = (o.status || "").toLowerCase();
        if (s in nextCounts) nextCounts[s] += 1;
      }
      setOrderCounts(nextCounts);

    } catch {
      setOrdersList([]);
    } finally {
      setOrdersListLoading(false);
    }
  };

  /* ---------- Room/Guest data ---------- */
  const fetchRoomData = async () => {
    // If guest is logged in but no QR parameters, use guest info
    if (isGuestLoggedIn && guestInfo && (!roomIdFromQR && !roomNumberFromQR)) {
      setLoading(true);
      try {
        // Try to fetch services using guest info
        const resolvedHotelId = guestInfo.hotelId;
        const roomNumber = guestInfo.roomNumber;

        const candidates = [
          `/RoomServices/ByRoomNumber/${encodeURIComponent(roomNumber)}?hotelId=${encodeURIComponent(resolvedHotelId)}`,
          `/RoomService/ByRoomNumber/${encodeURIComponent(roomNumber)}?hotelId=${encodeURIComponent(resolvedHotelId)}`,
        ];

        let data = null;
        for (const url of candidates) {
          try {
            const res = await axiosInstance.get(url, {
              validateStatus: (s) => [200, 400, 404].includes(s),
            });
            if (res.status === 200 && res.data) {
              data = res.data;
              break;
            }
          } catch (err) {
            //console.log(`Error fetching room data: ${err.message}`);
          }
        }

        if (data) {
          const { services: allServices } = data || {};
          /* CHANGED: normalize services */
          const all = Array.isArray(allServices) ? allServices.map(normalizeService) : [];

          const regularServices = [];
          const orderableServices = [];
          all.forEach((service) => {
            const hasMenu = service.menuItems.length > 0;
            if (hasMenu) orderableServices.push(service);
            else regularServices.push(service);
          });

          setServices(regularServices);
          setOrderServices(orderableServices);
          setBannerMessage("");
        } else {
          setServices([]);
          setOrderServices([]);
          setBannerMessage("No services available for your room.");
        }

        await fetchGuestOrder();
        fetchServiceRequests();
        fetchOrdersList();
        
      } catch (error) {
        setBannerMessage("Failed to load room services.");
        setBannerType("error");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Original QR-based room data fetching
    if ((!roomIdFromQR && !roomNumberFromQR) || !hotelId) {
      setBannerMessage("Invalid room or hotel ID.");
      setBannerType("error");
      setLoading(false);
      return;
    }

    const candidates = [];
    if (roomIdFromQR) {
      candidates.push(
        `/RoomServices/QR/${encodeURIComponent(roomIdFromQR)}?hotelId=${encodeURIComponent(hotelId)}`,
        `/RoomService/QR/${encodeURIComponent(roomIdFromQR)}?hotelId=${encodeURIComponent(hotelId)}`
      );
    }
    if (roomNumberFromQR) {
      candidates.push(
        `/RoomServices/QR/${encodeURIComponent(roomNumberFromQR)}?hotelId=${encodeURIComponent(hotelId)}`,
        `/RoomService/QR/${encodeURIComponent(roomNumberFromQR)}?hotelId=${encodeURIComponent(hotelId)}`,
        `/RoomServices/ByRoomNumber/${encodeURIComponent(roomNumberFromQR)}?hotelId=${encodeURIComponent(hotelId)}`,
        `/RoomService/ByRoomNumber/${encodeURIComponent(roomNumberFromQR)}?hotelId=${encodeURIComponent(hotelId)}`
      );
    }

    let data = null;
    let lastErr = null;

    for (const url of candidates) {
      try {
        const res = await axiosInstance.get(url, {
          validateStatus: (s) => [200, 400, 404].includes(s),
        });
        if (res.status === 200 && res.data) {
          data = res.data;
          break;
        }
        lastErr = res.data;
      } catch (err) {
        lastErr = err?.response?.data || err?.message;
      }
    }

    if (!data) {
      setGuestDetails(null);
      setServices([]);
      setOrderServices([]);
      setBannerMessage(
        (typeof lastErr === "string" && lastErr) || lastErr?.message || "Failed to load room details."
      );
      setBannerType("error");
      setLoading(false);
      return;
    }

    const { guest, services: allServices } = data || {};
    if (!guest) {
      setGuestDetails(null);
      setServices([]);
      setOrderServices([]);
      setBannerMessage("No guest details found.");
      setBannerType("error");
      setLoading(false);
      return;
    }

    const normalizedGuest = {
      hotelId: pick(guest, ["hotelId", "HotelId"]) ?? (hotelId ? Number(hotelId) : null),
      roomId: pick(guest, ["roomId", "RoomId"]) ?? (roomIdFromQR ? Number(roomIdFromQR) : null),
      roomNumber: String(pick(guest, ["roomNumber", "RoomNumber"]) ?? roomNumberFromQR ?? ""),
      guestId: pick(guest, ["guestId", "GuestId", "id", "Id"]) ?? null,
      fullName: pick(guest, ["fullName", "FullName", "name", "Name"]) || "",
      name: pick(guest, ["name", "Name"]) || "",
      nights: pick(guest, ["nights", "Nights"]) ?? null,
      currency: pick(guest, ["currency", "Currency"]) || "BWP",
      bookingId: pick(guest, ["bookingId", "BookingId"]) ?? (bookingIdFromQR ? Number(bookingIdFromQR) : null),
    };

    setGuestDetails(normalizedGuest);

    /* CHANGED: normalize services */
    const all = Array.isArray(allServices) ? allServices.map(normalizeService) : [];
    const regularServices = [];
    const orderableServices = [];
    all.forEach((service) => {
      const hasMenu = service.menuItems.length > 0;
      if (hasMenu) orderableServices.push(service);
      else regularServices.push(service);
    });

    setServices(regularServices);
    setOrderServices(orderableServices);

    await fetchGuestOrder(normalizedGuest.roomNumber);
    fetchServiceRequests(normalizedGuest.roomNumber, normalizedGuest.roomId);
    fetchOrdersList(normalizedGuest.roomNumber, normalizedGuest.bookingId || bookingIdFromQR);
    setBannerMessage("");
    setLoading(false);
  };

 
useEffect(() => {
 
  const token = localStorage.getItem('guestToken');
  const info  = safeParseJSON(localStorage.getItem('guestInfo'));
  if (token && info) {
    setIsGuestLoggedIn(true);
    setGuestInfo(info);
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    // clean up any corrupted leftovers so we don't keep erroring
    localStorage.removeItem('guestToken');
    localStorage.removeItem('guestInfo');
    setIsGuestLoggedIn(false);
  }
 
   setLoading(true);
   fetchRoomData();
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [roomIdFromQR, roomNumberFromQR, hotelId, bookingIdFromQR, isGuestLoggedIn]);

// In RoomQRPage.jsx - Add to your SignalR useEffect
useEffect(() => {
  const roomNumber = guestDetails?.roomNumber || guestInfo?.roomNumber;
  if (!roomNumber) return;

  let connection;
  let disposed = false;

  (async () => {
    try {
      connection = await startSignalRWithFallback();
      if (disposed) return;

      hubRef.current = connection;

      await connection.invoke("JoinRoom", String(roomNumber)).catch(() => {});

      // Add check-out listener
      connection.on("GuestCheckedOut", (payload) => {
        //console.log('Received GuestCheckedOut event:', payload);
        
        if (payload.roomNumber === roomNumber) {
          // Auto-logout the user
          antdMessage.warning('You have been checked out. Logging out...');
          setTimeout(() => {
            handleGuestLogout();
          }, 2000);
        }
      });

      // ... rest of your existing SignalR code
    } catch (err) {
      console.error("[SignalR] start failed:", err);
    }
  })();

  return () => {
    disposed = true;
    try {
      connection?.off("GuestCheckedOut");
      // ... other off calls
    } catch {}
    connection?.stop?.();
  };
}, [guestDetails?.roomNumber, guestInfo?.roomNumber]);

// Add this helper function to RoomQRPage.jsx
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to parse JWT token:', e);
    return null;
  }
};

 // In RoomQRPage.jsx - Update the initialization useEffect
useEffect(() => {
  const initializeApp = async () => {
    const token = localStorage.getItem('guestToken');
    const storedGuestInfo = safeParseJSON(localStorage.getItem('guestInfo'));
    
    if (token && storedGuestInfo) {
      //console.log('Found existing login:', storedGuestInfo);
      
      // Set authorization header
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      try {
        // Verify token is still valid by calling current endpoint
        const currentResponse = await axiosInstance.get('/GuestLogin/current');
        //console.log('Token valid, guest info:', currentResponse.data);
        
        setIsGuestLoggedIn(true);
        setGuestInfo(currentResponse.data);
      } catch (error) {
        //console.log('Token invalid or expired, auto-logging out:', error);
        
        // Auto-logout if token is invalid
        if (error.response?.status === 401 || error.response?.status === 403) {
          handleGuestLogout();
          antdMessage.info('Your session has expired. Please login again.');
        }
      }
    } else {
      // No valid login found
      setIsGuestLoggedIn(false);
      setGuestInfo(null);
    }

    setLoading(true);
    await fetchRoomData();
  };

  initializeApp();
}, [roomIdFromQR, roomNumberFromQR, hotelId, bookingIdFromQR]);


  /* ---------- Service Types (load when opening modal) ---------- */
  const fetchServiceTypes = async (serviceId) => {
    if (!serviceId) {
      setServiceTypes([]);
      return;
    }
    setServiceTypesLoading(true);
    try {
      const resolvedHotelId = hotelId || guestDetails?.hotelId || guestInfo?.hotelId;
      const res = await axiosInstance.get(`/RoomServiceTypes/by-service/${serviceId}`, {
        params: { hotelId: resolvedHotelId },
        validateStatus: (s) => [200, 204, 404].includes(s),
      });
      setServiceTypes(Array.isArray(res.data) ? res.data : []);
    } catch {
      setServiceTypes([]);
    } finally {
      setServiceTypesLoading(false);
    }
  };

  /* ---------- Service Modal controls ---------- */
 const openServiceModal = async (service) => {
  //console.log('Opening service modal for:', service?.name, service?.serviceId);
  
  if (!isGuestLoggedIn && !guestDetails) {
    setIsGuestLoginOpen(true);
    return;
  }
  
  /* CHANGED: ensure selected service is normalized */
  const s = normalizeService(service || {});
  //console.log('Normalized service:', s);
  
  setSelectedService(s);
  setServiceNotes("");
  setServiceQty(null);
  setSelectedTypeId(null);
  await fetchServiceTypes(s?.serviceId);
  setIsServiceModalOpen(true);
};


  const closeServiceModal = () => {
    setSelectedService(null);
    setServiceNotes("");
    setServiceQty(null);
    setSelectedTypeId(null);
    setIsServiceModalOpen(false);
  };

  /* ---------- Order Modal controls ---------- */
const openOrderModal = (service) => {
  //console.log('Opening order modal for:', service?.name, service?.serviceId);
  
  if (!isGuestLoggedIn && !guestDetails) {
    setIsGuestLoginOpen(true);
    return;
  }
    /* CHANGED: normalize before using */
    setSelectedOrderService(normalizeService(service || {}));
    setOrderNotes("");
    setOrderQty({});
    setServiceMode("RoomService");
    setPickupLocation("Restaurant Counter");
    setIsOrderModalOpen(true);
  };

  const closeOrderModal = () => {
    setSelectedOrderService(null);
    setOrderNotes("");
    setOrderQty({});
    setIsOrderModalOpen(false);
  };

  // ----- Service validation & submit -----
  const validateService = () => {
    if (!isGuestLoggedIn && !guestDetails) {
      antdMessage.error("Please login first.");
      return false;
    }
    if (!selectedService?.serviceId) {
      antdMessage.error("Invalid service selected.");
      return false;
    }
    if (!selectedTypeId && !serviceNotes.trim()) {
      antdMessage.warning("Pick a service type or describe your request.");
      return false;
    }
    return true;
  };

  const handleServiceSubmit = async () => {
    if (!validateService()) return;

    const chosenType = serviceTypes.find((t) => t.roomServiceTypeId === selectedTypeId);
    const typeName = chosenType?.name || selectedService?.name || "Service";

    const qtyText = serviceQty ? ` x${asNum(serviceQty)}` : "";
    const notesText = serviceNotes.trim() ? ` — ${serviceNotes.trim()}` : "";
    const description = `${typeName}${qtyText}${notesText}`;

    const resolvedRoomId = guestDetails?.roomId ?? (roomIdFromQR ? Number(roomIdFromQR) : null);
    const resolvedRoomNumber = guestDetails?.roomNumber ?? roomNumberFromQR ?? guestInfo?.roomNumber;

    if (!resolvedRoomId && !resolvedRoomNumber) {
      antdMessage.error("Missing room identifier (RoomId or RoomNumber).");
      return;
    }

    const resolvedHotelId = hotelId || guestDetails?.hotelId || guestInfo?.hotelId;

    const payload = {
      HotelId: resolvedHotelId ? Number(resolvedHotelId) : null,
      RoomId: resolvedRoomId,
      RoomNumber: resolvedRoomNumber,
      GuestId: guestDetails?.guestId ?? null,
      ServiceId: selectedService?.serviceId ?? null,
      ServiceType: typeName,
      ServiceDescription: description,
      ServiceName: typeName,
      Description: description,
      Notes: serviceNotes.trim() || null,
      RequestTime: new Date().toISOString(),
      ServiceDate: new Date().toISOString().slice(0, 10),
      OrderItems: [],
    };

    try {
      setIsSubmittingService(true);
      await axiosInstance.post("/RoomServices/Request", payload, {
        params: {
          hotelId: resolvedHotelId,
          roomId: payload.RoomId || undefined,
          roomNumber: payload.RoomNumber || undefined,
        },
      });

      // also mirror in chat
      try {
        const chatPayload = {
          sender: "guest",
          room: String(resolvedRoomNumber || ""),
          text: `Requested service: ${description}`,
          hotelId: resolvedHotelId,
        };
        const chatRes = await axiosInstance.post("/Chat", chatPayload);
        setChatMessages((prev) => [...prev, chatRes.data ?? chatPayload]);
      } catch {}

      fetchServiceRequests(resolvedRoomNumber, resolvedRoomId);

      antdMessage.success("Service request submitted.");
      setBannerMessage("Service request sent successfully.");
      setBannerType("success");
      closeServiceModal();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors) {
        const flat = Object.entries(apiErrors)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
          .join(" • ");
        antdMessage.error(flat || "Failed to send service request.");
        setBannerMessage(flat || "Failed to send service request.");
      } else {
        const msg =
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null) ||
          "Failed to send service request.";
        antdMessage.error(msg);
        setBannerMessage(msg);
      }
      setBannerType("error");
    } finally {
      setIsSubmittingService(false);
    }
  };

  // ----- Order validation & submit -----
  const validateOrder = () => {
    if (!isGuestLoggedIn && !guestDetails) {
      antdMessage.error("Please login first.");
      return false;
    }
    if (!selectedOrderService?.serviceId) {
      antdMessage.error("Invalid service selected.");
      return false;
    }
    if (orderItemsCount === 0 && !orderNotes.trim()) {
      antdMessage.warning("Add at least one item or include a note.");
      return false;
    }
    if (serviceMode === "Collection" && !pickupLocation.trim()) {
      antdMessage.warning("Please enter a pickup location.");
      return false;
    }
    return true;
  };

  const handleOrderSubmit = async () => {
    if (!validateOrder()) return;

    let cleanNotes = orderNotes.trim();

 const selected = orderMenu
  .filter((m) => asNum(orderQty[m.uid]) > 0)
  .map((m) => ({
    MenuItemId: asNum(m.menuItemId) || 0,   // API id (may be 0 if your API allows it)
    Name: m.name,
    Description: m.description,
    Price: asNum(m.price),
    EstimatedReadyTime: m.estimatedReadyTime,
    Quantity: asNum(orderQty[m.uid]),
  }));

    if (selected.length > 0 && !cleanNotes) {
      cleanNotes = "Ordered items: " + selected.map((i) => `${i.Name} (x${i.Quantity})`).join(", ");
    }

    const resolvedHotelId = hotelId || guestDetails?.hotelId || guestInfo?.hotelId;
    const roomNumber = guestDetails?.roomNumber || guestInfo?.roomNumber;

    const payload = {
      RoomNumber: roomNumber,
      GuestId: guestDetails?.guestId ?? null,
      Description: cleanNotes || "Restaurant order",
      OrderType: selectedOrderService?.name || "Room Service",
      CreatedBy: "Guest",
      Status: "Pending",
      OrderItems: selected.length > 0 ? selected : [],
      DeliveryMethod: serviceMode, // "RoomService" | "Collection"
      PickupLocation: serviceMode === "Collection" ? pickupLocation : null,
      HotelId: resolvedHotelId ? Number(resolvedHotelId) : null,
    };

    try {
      setIsSubmittingOrder(true);
      
      // Use the correct URL pattern with hotelId in the route
      await axiosInstance.post(`/hotels/${resolvedHotelId}/RestaurantOrders`, payload);
      
      antdMessage.success("Order submitted.");
      setBannerMessage("Order sent successfully.");
      setBannerType("success");
      closeOrderModal();

      setOrderCounts((c) => ({ ...c, pending: c.pending + 1 }));
      await fetchGuestOrder(roomNumber);
      fetchOrdersList(roomNumber, guestDetails?.bookingId || bookingIdFromQR);
      setBottomTab("current");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to send order.";
      antdMessage.error(msg);
      setBannerMessage(msg);
      setBannerType("error");
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  /* ---------- 🔔 SignalR: connect & listen ---------- */
  useEffect(() => {
    const roomNumber = guestDetails?.roomNumber || guestInfo?.roomNumber;
    if (!roomNumber) return;

    let connection;
    let disposed = false;

    (async () => {
      try {
        connection = await startSignalRWithFallback();
        if (disposed) return;

        hubRef.current = connection;

        await connection.invoke("JoinRoom", String(roomNumber)).catch(() => {});

        connection.on("ReceiveRoomService", (room, message) => {
          //console.log("[SignalR] ReceiveRoomService:", room, message);
        });

        connection.on("RestaurantOrderStatus", async (payload) => {
          const p = {
            orderId: pick(payload, ["orderId", "OrderId"]),
            roomNumber: String(pick(payload, ["roomNumber", "RoomNumber"]) || ""),
            status: (pick(payload, ["status", "Status"]) || "").toLowerCase(),
            delivery: pick(payload, ["deliveryMethod", "DeliveryMethod"]),
            pickup: pick(payload, ["pickupLocation", "PickupLocation"]),
            rejection: pick(payload, ["rejectionReason", "RejectionReason"]),
          };

          if (!p.roomNumber || p.roomNumber !== String(roomNumber)) return;

          setOrderCounts((c) => {
            const next = { ...c };
            if (p.status === "accepted") {
              next.accepted += 1;
              if (next.pending > 0) next.pending -= 1;
            } else if (p.status === "completed") {
              next.completed += 1;
            } else if (p.status === "rejected") {
              next.rejected += 1;
              if (next.pending > 0) next.pending -= 1;
            }
            return next;
          });

          let desc = `Order #${p.orderId} is ${p.status}.`;
          if (p.status === "accepted") {
            desc =
              p.delivery === "Collection"
                ? `Order #${p.orderId} accepted. You'll be notified when it's ready for collection${p.pickup ? ` at ${p.pickup}` : ""}.`
                : `Order #${p.orderId} accepted. We'll deliver to your room shortly.`;
          } else if (p.status === "rejected") {
            desc = `Order #${p.orderId} was rejected${p.rejection ? `: ${p.rejection}` : "."}`;
          } else if (p.status === "completed") {
            desc =
              p.delivery === "Collection"
                ? `Order #${p.orderId} is ready for collection${p.pickup ? ` at ${p.pickup}` : ""}.`
                : `Order #${p.orderId} is on its way to your room.`;
          }

          api.open({
            message: "Order Update",
            description: desc,
            placement: "bottomRight",
          });

          if (p.orderId) {
            await setOrderFromId(p.orderId);
          } else {
            await fetchGuestOrder(roomNumber);
          }
          fetchOrdersList(roomNumber, guestDetails?.bookingId || bookingIdFromQR);
        });

        connection.on("OrderMetrics", (m) => {
          if (!m) return;
          const rn = String(pick(m, ["roomNumber", "RoomNumber"]) || "");
          if (rn && rn !== String(roomNumber)) return;

          setOrderCounts({
            pending: asNum(pick(m, ["pending", "Pending"])) || 0,
            accepted: asNum(pick(m, ["accepted", "Accepted"])) || 0,
            completed: asNum(pick(m, ["completed", "Completed"])) || 0,
            rejected: asNum(pick(m, ["rejected", "Rejected"])) || 0,
          });
        });
      } catch (err) {
        console.error("[SignalR] start failed:", err);
      }
    })();

    return () => {
      disposed = true;
      try {
        connection?.off("RestaurantOrderStatus");
        connection?.off("ReceiveRoomService");
        connection?.off("OrderMetrics");
      } catch {}
      connection?.stop?.();
    };
  }, [guestDetails?.roomNumber, guestInfo?.roomNumber, api, bookingIdFromQR]);

  // ----- UI blocks for Order Modal -----
  const OrderMenuTab = (
    <>
      {orderMenu.length === 0 ? (
        <Empty description="No menu items available." />
      ) : (
        <List
  itemLayout="vertical"
  dataSource={orderMenu}
  renderItem={(m) => (
    <Card key={m.uid} size="small" styles={{ body: { padding: 12 } }} style={{ marginBottom: 12 }}>
      <Row gutter={[8, 8]} align="middle">
        <Col flex="auto">
          <Space direction="vertical" size={2}>
            <Text strong>{m.name}</Text>
            {m.description && (
              <Text type="secondary" style={{ display: "block" }}>
                {m.description}
              </Text>
            )}
            <Space size="small" wrap>
              <Tag color="blue">
                {money(m.price, guestDetails?.currency || "BWP")}
              </Tag>
              {m.estimatedReadyTime && <Tag>{m.estimatedReadyTime} min</Tag>}
            </Space>
          </Space>
        </Col>
        <Col>
          <Space.Compact>
            <Button
              size="small"
              icon={<MinusOutlined />}
              onClick={() => dec(m.uid)}
              disabled={isSubmittingOrder || !asNum(orderQty[m.uid])}
            />
            <InputNumber
              min={0}
              value={asNum(orderQty[m.uid] || 0)}
              onChange={(v) => setItemQty(m.uid, v)}
              controls={false}
              style={{ width: 64, textAlign: "center" }}
              disabled={isSubmittingOrder}
            />
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => inc(m.uid)}
              disabled={isSubmittingOrder}
            />
          </Space.Compact>
        </Col>
      </Row>
    </Card>
  )}
/>
      )}

      <Divider style={{ margin: "12px 0" }} />

      {/* Fulfilment choice */}
      <Card size="small" styles={{ body: { padding: 12 } }} style={{ marginBottom: 12 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text strong>Fulfilment</Text>
          <Radio.Group
            value={serviceMode}
            onChange={(e) => setServiceMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="RoomService">Room service (deliver)</Radio.Button>
            <Radio.Button value="Collection">Collection (pickup)</Radio.Button>
          </Radio.Group>

          {serviceMode === "Collection" ? (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text type="secondary">Pickup location</Text>
              <Input
                placeholder="e.g., Restaurant Counter / Lobby Bar"
                value={pickupLocation}
                onChange={(e) => setPickupLocation(e.target.value)}
                disabled={isSubmittingOrder}
              />
            </Space>
          ) : (
            <Text type="secondary">
              We'll deliver to <Tag style={{ marginLeft: 4 }}>Room {guestDetails?.roomNumber || guestInfo?.roomNumber}</Tag>
            </Text>
          )}
        </Space>
      </Card>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={14}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">Add a note (optional)</Text>
            <TextArea
              rows={3}
              maxLength={500}
              showCount
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="E.g., Please bring extra napkins."
              disabled={isSubmittingOrder}
            />
          </Space>
        </Col>
        <Col xs={24} md={10}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Space style={{ justifyContent: "space-between", width: "100%" }}>
                <Text type="secondary">Total</Text>
                <Title level={screens.xs ? 5 : 4} style={{ margin: 0 }}>
                  {money(orderTotal, guestDetails?.currency || "BWP")}
                </Title>
              </Space>
              <Text type="secondary">Items selected: {orderItemsCount}</Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </>
  );

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          padding: isMobile ? 8 : 16,
        }}
      >
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Text type="secondary">Loading room information…</Text>
        </Space>
      </div>
    );
  }

  const chatDrawerWidth = isMobile ? "100%" : 380;

  // Normalize order for display
  const normalizedOrder = guestOrder
    ? {
        orderId: pick(guestOrder, ["orderId", "OrderId"]),
        roomNumber: pick(guestOrder, ["roomNumber", "RoomNumber"]),
        amount: asNum(pick(guestOrder, ["amount", "Amount"])),
        createdDate: pick(guestOrder, ["createdDate", "CreatedDate"]),
        status: pick(guestOrder, ["status", "Status"]),
        deliveryMethod: pick(guestOrder, ["deliveryMethod", "DeliveryMethod"]),
        pickupLocation: pick(guestOrder, ["pickupLocation", "PickupLocation"]),
        items:
          pick(guestOrder, [
            "orderItems",
            "OrderItems",
            "restaurantOrderItems",
            "RestaurantOrderItems",
          ]) ?? [],
      }
    : null;

  const items = Array.isArray(normalizedOrder?.items)
    ? normalizedOrder.items.map((i) => ({
        id:
          pick(i, [
            "restaurantOrderItemId",
            "RestaurantOrderItemId",
            "id",
            "Id",
          ]) ?? pick(i, ["menuItemId", "MenuItemId"]),
        name: pick(i, ["name", "Name"]) ?? "",
        price: asNum(pick(i, ["price", "Price"])),
        quantity: asNum(pick(i, ["quantity", "Quantity"])),
      }))
    : [];

  const serviceCols = [
    { title: "Requested", dataIndex: "createdDate", key: "createdDate", render: fmtDT },
    { title: "Service", dataIndex: "serviceType", key: "serviceType" },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (t) => (t ? <span style={{ whiteSpace: "pre-wrap" }}>{t}</span> : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag
          color={
            s?.toLowerCase() === "completed"
              ? "green"
              : s?.toLowerCase() === "rejected"
              ? "red"
              : "processing"
          }
        >
          {s || "Pending"}
        </Tag>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      align: "right",
      render: (q) => (q ? `x${q}` : "-"),
    },
  ];

  const ordersCols = [
    { title: "Order #", dataIndex: "orderId", key: "orderId", width: 110 },
    { title: "Type", dataIndex: "orderType", key: "orderType" },
    {
      title: "Items",
      dataIndex: "itemsCount",
      key: "itemsCount",
      width: 90,
      align: "right",
      render: (n) => n ?? 0,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: (a) => money(a, guestDetails?.currency || "BWP"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag
          color={
            (s || "").toLowerCase() === "completed"
              ? "green"
              : (s || "").toLowerCase() === "rejected"
              ? "red"
              : (s || "").toLowerCase() === "accepted"
              ? "blue"
              : "default"
          }
        >
          {s || "Pending"}
        </Tag>
      ),
    },
    { title: "Created", dataIndex: "createdDate", key: "createdDate", render: fmtDT },
    {
      title: "Delivery",
      dataIndex: "deliveryMethod",
      key: "deliveryMethod",
      render: (d, r) =>
        d === "Collection" ? (
          <Space size="small">
            <Tag>Collection</Tag>
            {r?.pickupLocation && <Tag>{r.pickupLocation}</Tag>}
          </Space>
        ) : d ? (
          <Tag color="processing">Room service</Tag>
        ) : (
          "-"
        ),
    },
  ];

  // Show login screen if not logged in and no guest details from QR
  if (!isGuestLoggedIn ) { //&& !guestDetails
    return (
      <div
        style={{
          padding: isMobile ? 8 : 16,
          maxWidth: 1200,
          margin: "0 auto",
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          style={{
            maxWidth: 400,
            width: "100%",
            textAlign: "center",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <LockOutlined style={{ fontSize: 48, color: "#1890ff" }} />
            <Title level={3}>Room Services</Title>
            <Text type="secondary">
              Please login to access room services, order food & drinks, and manage your stay
            </Text>
            <Button 
              type="primary" 
              size="large" 
              onClick={() => setIsGuestLoginOpen(true)}
              style={{ width: "100%", height: 45 }}
            >
              Guest Login
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Use your ID Number and password to login
            </Text>
          </Space>
        </Card>

        <GuestLogin
          visible={isGuestLoginOpen}
          onClose={() => setIsGuestLoginOpen(false)}
          onLoginSuccess={handleGuestLoginSuccess}
          hotelId={hotelId}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? 8 : 16,
        maxWidth: 1200,
        margin: "0 auto",
      }}
    >
      {contextHolder}

      {/* Header */}
      <Card
        style={{
          marginBottom: 16,
          background:
            "linear-gradient(135deg, rgba(24,144,255,0.1) 0%, rgba(111,66,193,0.08) 100%)",
          border: "1px solid #f0f0f0",
          borderRadius: 12,
        }}
        styles={{ body: { padding: isMobile ? 12 : 20 } }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={2}>
              <Title level={isMobile ? 4 : 3} style={{ marginBottom: 4 }}>
                {guestDetails || guestInfo ? `Hello, ${guestName}!` : "Welcome!"}
              </Title>
              <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14 }}>
                {guestDetails || guestInfo
                  ? `Welcome to Room ${guestDetails?.roomNumber || guestInfo?.roomNumber}. We're here to make your stay more comfortable.`
                  : "Scan your room QR to see available services."}
              </Text>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              <Tag color="blue" icon={<UserOutlined />}>
                Room {guestDetails?.roomNumber || guestInfo?.roomNumber}
              </Tag>
              {guestDetails?.nights != null && (
                <Tag color="purple">{guestDetails.nights} night(s)</Tag>
              )}
              {isGuestLoggedIn && (
                <Button 
                  size="small" 
                  onClick={handleGuestLogout}
                  type="text"
                  danger
                >
                  Logout
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {(guestDetails || isGuestLoggedIn) ? (
        <>
          {/* Services & Order Food & Drinks as Tabs */}
          <Card 
            style={{ borderRadius: 12, marginBottom: 16 }}
            styles={{ body: { paddingTop: 0 } }}
          >
            <Tabs
              activeKey={servicesTab}
              onChange={setServicesTab}
              items={[
                {
                  key: "services",
                  label: (
                    <Space>
                      <BellOutlined />
                      <span>Services</span>
                    </Space>
                  ),
                  children: (
                    <div style={{ paddingTop: 12 }}>
                      {services.length === 0 ? (
                        <Empty description="No services available at the moment." />
                      ) : (
                        <Row gutter={[12, 12]}>
                          {services.map((service) => (
                            <Col key={service.serviceId} xs={24}>
                              <ServiceCard
                                service={service}
                                onRequest={openServiceModal}
                                isMobile={isMobile}
                                type="service"
                              />
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  ),
                },
                {
                  key: "orders",
                  label: (
                    <Space>
                      <ShoppingCartOutlined />
                      <span>Order Food & Drinks</span>
                    </Space>
                  ),
                  children: (
                    <div style={{ paddingTop: 12 }}>
                      {orderServices.length === 0 ? (
                        <Empty description="No order services available at the moment." />
                      ) : (
                        <Row gutter={[12, 12]}>
                          {orderServices.map((service) => (
                            <Col key={service.serviceId} xs={24}>
                              <ServiceCard
                                service={service}
                                onRequest={openOrderModal}
                                isMobile={isMobile}
                                type="order"
                              />
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>

          {/* Collapsible Live Order Activity + Tabs */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={8}>
              <LiveOrderChart 
                counts={orderCounts} 
                collapsed={chartCollapsed}
                onToggle={() => setChartCollapsed(!chartCollapsed)}
              />
            </Col>
            <Col xs={24} md={16}>
              {/* BOTTOM: Horizontal tab menu for tables & status */}
              <Card style={{ borderRadius: 12 }} title="My Stay — Details">
                <Tabs
                  activeKey={bottomTab}
                  onChange={setBottomTab}
                  items={[
                    {
                      key: "current",
                      label: "Current Order & Activity",
                      children: (
                        <Space direction="vertical" style={{ width: "100%" }} size="large">
                          <Card
                            title="My Current Orders"
                            extra={
                              <Badge
                                color={guestOrder ? "green" : "default"}
                                text={normalizedOrder?.status || "None"}
                              />
                            }
                            style={{ borderRadius: 12 }}
                          >
                            {orderLoading ? (
                              <div style={{ display: "grid", placeItems: "center", padding: 24 }}>
                                <Spin />
                              </div>
                            ) : normalizedOrder ? (
                              <Space direction="vertical" style={{ width: "100%" }} size="large">
                                <Row gutter={[12, 12]}>
                                  <Col xs={12} md={24}>
                                    <Statistic
                                      title="Order ID"
                                      value={normalizedOrder.orderId}
                                      valueStyle={{ fontSize: 16 }}
                                    />
                                  </Col>
                                  <Col xs={12} md={24}>
                                    <Statistic
                                      title="Room"
                                      value={normalizedOrder.roomNumber}
                                      valueStyle={{ fontSize: 16 }}
                                    />
                                  </Col>
                                  <Col xs={12} md={24}>
                                    <Statistic
                                      title="Total"
                                      value={asNum(normalizedOrder.amount).toFixed(2)}
                                      prefix="BWP "
                                      valueStyle={{ fontSize: 16 }}
                                    />
                                  </Col>
                                  <Col xs={12} md={24}>
                                    <Statistic
                                      title="Date"
                                      value={fmtDT(normalizedOrder.createdDate)}
                                      valueStyle={{ fontSize: 14 }}
                                    />
                                  </Col>
                                </Row>

                                <Space>
                                  <Tag color="processing">
                                    {normalizedOrder?.deliveryMethod === "Collection"
                                      ? "Collection"
                                      : "Room service"}
                                  </Tag>
                                  {normalizedOrder?.deliveryMethod === "Collection" &&
                                    normalizedOrder?.pickupLocation && (
                                      <Tag>{normalizedOrder.pickupLocation}</Tag>
                                    )}
                                </Space>

                                {Array.isArray(items) && items.length > 0 ? (
                                  <List
                                    size="small"
                                    header={<Text strong>Order Items</Text>}
                                    dataSource={items}
                                    renderItem={(item) => (
                                      <List.Item>
                                        <Space
                                          style={{ width: "100%", justifyContent: "space-between" }}
                                          wrap
                                        >
                                          <Space>
                                            <Text style={{ fontSize: 14 }}>{item.name}</Text>
                                            <Tag>x{item.quantity}</Tag>
                                          </Space>
                                          <Text type="secondary" style={{ fontSize: 14 }}>
                                            BWP {asNum(item.price).toFixed(2)} each
                                          </Text>
                                        </Space>
                                      </List.Item>
                                    )}
                                  />
                                ) : (
                                  <Text type="secondary">No items in this order.</Text>
                                )}
                              </Space>
                            ) : (
                              <Empty description="You currently have no active orders." />
                            )}
                          </Card>
                        </Space>
                      ),
                    },
                    {
                      key: "requests",
                      label: "Services Requested",
                      children: (
                        <Table
                          size="small"
                          rowKey="id"
                          columns={serviceCols}
                          dataSource={serviceRequests}
                          loading={serviceRequestsLoading}
                          pagination={{ pageSize: 6, size: "small" }}
                        />
                      ),
                    },
                    {
                      key: "history",
                      label: "Order History",
                      children: (
                        <div>
                          {/* Mobile-friendly order cards */}
                          {isMobile ? (
                            <Space direction="vertical" style={{ width: "100%" }} size="middle">
                              {ordersList.length === 0 ? (
                                <Empty description="No order history found" />
                              ) : (
                                ordersList.map((order) => (
                                  <Card
                                    key={order.orderId}
                                    size="small"
                                    styles={{ 
                                      body: { 
                                        padding: 12,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8
                                      } 
                                    }}
                                    style={{ borderRadius: 8 }}
                                  >
                                    {/* Header row */}
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                      flexWrap: 'wrap',
                                      gap: 8
                                    }}>
                                      <Space direction="vertical" size={2}>
                                        <Text strong style={{ fontSize: 14 }}>Order #{order.orderId}</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {fmtDT(order.createdDate)}
                                        </Text>
                                      </Space>
                                      <Tag
                                        color={
                                          (order.status || "").toLowerCase() === "completed"
                                            ? "green"
                                            : (order.status || "").toLowerCase() === "rejected"
                                            ? "red"
                                            : (order.status || "").toLowerCase() === "accepted"
                                            ? "blue"
                                            : "default"
                                        }
                                        style={{ margin: 0 }}
                                      >
                                        {order.status || "Pending"}
                                      </Tag>
                                    </div>

                                    {/* Order details */}
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                      gap: 8
                                    }}>
                                      <Space direction="vertical" size={2}>
                                        <Text style={{ fontSize: 13 }}>{order.orderType}</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {order.itemsCount} item{order.itemsCount !== 1 ? 's' : ''}
                                        </Text>
                                      </Space>
                                      <Text strong style={{ fontSize: 14 }}>
                                        {money(order.amount, guestDetails?.currency || "BWP")}
                                      </Text>
                                    </div>

                                    {/* Delivery method */}
                                    <div>
                                      {order.deliveryMethod === "Collection" ? (
                                        <Space size="small" wrap>
                                          <Tag style={{ fontSize: 11, margin: 0 }}>Collection</Tag>
                                          {order?.pickupLocation && (
                                            <Tag style={{ fontSize: 11, margin: 0 }}>{order.pickupLocation}</Tag>
                                          )}
                                        </Space>
                                      ) : order.deliveryMethod ? (
                                        <Tag color="processing" style={{ fontSize: 11, margin: 0 }}>
                                          Room service
                                        </Tag>
                                      ) : (
                                        <Text type="secondary" style={{ fontSize: 11 }}>Delivery not specified</Text>
                                      )}
                                    </div>
                                  </Card>
                                ))
                              )}
                            </Space>
                          ) : (
                            /* Desktop table */
                            <Table
                              size="small"
                              rowKey="orderId"
                              columns={ordersCols}
                              dataSource={ordersList}
                              loading={ordersListLoading}
                              pagination={{ pageSize: 6, size: "small" }}
                            />
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          {/* Service Request Modal */}
          <Modal
            open={isServiceModalOpen}
            onCancel={closeServiceModal}
            /* CHANGED: correct prop */
            destroyOnClose
            title={
              <Space>
                <CustomerServiceOutlined />
                <span>Request {selectedService?.name || "Service"}</span>
              </Space>
            }
            width={screens.md ? 600 : "100%"}
            footer={
              <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                <Button onClick={closeServiceModal} disabled={isSubmittingService}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={handleServiceSubmit}
                  loading={isSubmittingService}
                  disabled={isSubmittingService || (!selectedTypeId && !serviceNotes.trim())}
                >
                  Submit Request
                </Button>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>What do you need?</Text>
              <Select
                loading={serviceTypesLoading}
                placeholder={
                  serviceTypesLoading ? "Loading types…" : "Select a service type (e.g., Extra towels)"
                }
                value={selectedTypeId ?? undefined}
                onChange={(v) => setSelectedTypeId(v ?? null)}
                allowClear
                options={(serviceTypes || []).map((t) => ({
                  value: t.roomServiceTypeId,
                  label: t.name + (t.category ? ` — ${t.category}` : ""),
                }))}
              />
              <Space size="small">
                <Text type="secondary">Quantity (optional)</Text>
                <InputNumber min={1} value={serviceQty} onChange={(v) => setServiceQty(v)} style={{ width: 120 }} />
              </Space>
              <Text type="secondary">Additional details (optional)</Text>
              <TextArea
                rows={5}
                maxLength={1000}
                showCount
                value={serviceNotes}
                onChange={(e) => setServiceNotes(e.target.value)}
                placeholder="E.g., 2 extra bath towels, please."
                disabled={isSubmittingService}
              />
            </Space>
          </Modal>

          {/* Order Modal */}
          <Modal
            open={isOrderModalOpen}
            onCancel={closeOrderModal}
            /* CHANGED: correct prop */
            destroyOnClose
            title={
              <Space>
                <ShoppingCartOutlined />
                <span>Order from {selectedOrderService?.name || "Menu"}</span>
              </Space>
            }
            width={screens.md ? 760 : "100%"}
            styles={{
              body: {
                maxHeight: "calc(100dvh - 200px)",
                overflowY: "auto",
                paddingTop: 8,
              },
            }}
            footer={
              <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                <Button onClick={closeOrderModal} disabled={isSubmittingOrder}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={handleOrderSubmit}
                  loading={isSubmittingOrder}
                  disabled={isSubmittingOrder || (orderItemsCount === 0 && !orderNotes.trim())}
                >
                  Place Order
                </Button>
              </Space>
            }
          >
            {OrderMenuTab}
          </Modal>
        </>
      ) : (
        <Card style={{ borderRadius: 12 }}>
          <Empty description={bannerMessage || "No guest details found."} />
        </Card>
      )}

      {/* Floating Chat Button */}
      <FloatButton
        icon={<MessageOutlined />}
        type="primary"
        tooltip={<div>Chat with Reception</div>}
        onClick={() => setChatOpen(true)}
        style={{
          right: isMobile ? 16 : 24,
          bottom: isMobile ? 80 : 24,
          width: isMobile ? 50 : 56,
          height: isMobile ? 50 : 56,
        }}
      />

      {/* Chat Drawer */}
      <Drawer
        title={
          <Space>
            <MessageOutlined />
            Chat with Reception
            <Badge status="processing" text="Online" />
          </Space>
        }
        placement="right"
        width={chatDrawerWidth}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        destroyOnClose
        closeIcon={<CloseOutlined />}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            padding: isMobile ? 12 : 16,
            paddingBottom: 8,
          },
          header: {
            padding: isMobile ? "12px 16px" : "16px 24px",
            borderBottom: "1px solid #f0f0f0",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            height: "100%",
            overflowY: "auto",
            paddingRight: 4,
            marginBottom: 12,
          }}
        >
          {chatMessages.map((msg, idx) => {
            const isGuest = msg.sender === "guest";
            return (
              <div
                key={idx}
                style={{
                  alignSelf: isGuest ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                  background: isGuest ? "#1677ff" : "#f5f5f5",
                  color: isGuest ? "#fff" : "inherit",
                  padding: "8px 12px",
                  borderRadius: 12,
                  wordBreak: "break-word",
                }}
              >
                <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{msg.text}</div>
                <div
                  style={{
                    fontSize: 10,
                    opacity: 0.75,
                    marginTop: 4,
                    textAlign: "right",
                  }}
                >
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ""}
                </div>
              </div>
            );
          })}
          {chatMessages.length === 0 && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No messages yet" style={{ marginTop: 40 }} />
          )}
        </div>

        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Type your message…"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onPressEnter={handleSendMessage}
          />
          <Button type="primary" icon={<SendOutlined />} onClick={handleSendMessage}>
            Send
          </Button>
        </Space.Compact>
      </Drawer>

      {/* Guest Login Modal */}
      <GuestLogin
        visible={isGuestLoginOpen}
        onClose={() => setIsGuestLoginOpen(false)}
        onLoginSuccess={handleGuestLoginSuccess}
        hotelId={hotelId}
      />
    </div>
  );
};

export default RoomQRPage;
